/*
 * The state-machine progress rail. Renders the run's path through the 17 states
 * as an ordered milestone line (done / active / pending), with the current run
 * status badge and any overlay/branch state (paused, recovering, needs_reauth)
 * surfaced beneath. Live updates arrive via the `status` prop, which the run
 * detail page keeps in sync from the WS stream + the run query.
 */

import { StatusPill } from "../StatusPill";
import { resolvePipeline, overlayState } from "./stages";
import styles from "./pipeline.module.css";

function norm(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

const STALLED = new Set(["failed", "aborted"]);

export interface PipelineProgressProps {
  /** Current run status (the 17-state enum value). */
  status: string;
  /** Control overlay (none/pausing/paused/cancelling), for the badge row. */
  control?: string;
  /** Optional current backend stage string (from `current_stage` / WS frame). */
  currentStage?: string | null;
}

export function PipelineProgress({
  status,
  control,
  currentStage,
}: PipelineProgressProps) {
  const stages = resolvePipeline(status, currentStage);
  const overlay = overlayState(status);
  const stalled = STALLED.has(norm(status));

  return (
    <section className={styles.panel} aria-label="Pipeline progress">
      <ol className={styles.rail}>
        {stages.map((stage, i) => {
          const stepClass = [
            styles.step,
            stage.status === "done" && styles.stepDone,
            stage.status === "active" && styles.stepActive,
            stage.status === "active" && stalled && styles.stepStalled,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={stage.key} className={stepClass} aria-current={stage.status === "active" || undefined}>
              <span className={styles.dot} aria-hidden="true">
                {stage.status === "done" ? "✓" : i + 1}
              </span>
              <span className={styles.stepLabel}>{stage.label}</span>
            </li>
          );
        })}
      </ol>

      <div className={styles.railFooter}>
        <StatusPill kind="run" value={status} />
        {overlay && overlay.state !== norm(status) && (
          <StatusPill kind="run" value={overlay.state} />
        )}
        {control && norm(control) !== "none" && norm(control) !== "" && (
          <StatusPill
            tone={norm(control) === "cancelling" ? "danger" : "warning"}
            label={norm(control) === "cancelling" ? "Cancelling…" : "Pausing…"}
          />
        )}
        {currentStage && (
          <span className={styles.railMeta}>
            Stage: <code>{currentStage}</code>
          </span>
        )}
      </div>
    </section>
  );
}
